/**
 * Promo link helpers — capture, persist, and auto-apply a promo code from URL params.
 *
 * Flow:
 *   1. A reader lands on /?promo=TRIAL30 (or any page with ?promo=X) from a blog/email/share.
 *   2. capturePromoFromUrl() detects it, stores it with a 14-day TTL in localStorage.
 *   3. On the LoginPage register tab and the UpgradePage, getStoredPromo() pre-fills
 *      the existing promo input so the code is applied at checkout / registration.
 *   4. clearStoredPromo() should be called after successful redemption to avoid stale state.
 *
 * No backend round-trip on capture — we trust the URL and let the existing
 * /api/auth/validate-discount call (already wired on both pages) reject invalid codes.
 */

const KEY = 'ds_promo_v1';
const TTL_DAYS = 14;

export function capturePromoFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('promo') || '').trim().toUpperCase();
    if (!code) return null;
    // Sanitize: alphanumeric + dash/underscore only, max 32 chars
    const safe = code.replace(/[^A-Z0-9_-]/g, '').slice(0, 32);
    if (!safe) return null;
    const payload = { code: safe, captured_at: Date.now() };
    window.localStorage.setItem(KEY, JSON.stringify(payload));
    return safe;
  } catch {
    return null;
  }
}

export function getStoredPromo() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const { code, captured_at } = JSON.parse(raw);
    if (!code || !captured_at) return null;
    const ageDays = (Date.now() - captured_at) / (1000 * 60 * 60 * 24);
    if (ageDays > TTL_DAYS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

export function clearStoredPromo() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {}
}

/** Build a "Try Diamond Scorer" link with a promo query param appended. */
export function withPromo(path, code) {
  const promo = code || getStoredPromo();
  if (!promo) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}promo=${encodeURIComponent(promo)}`;
}
